import { IconButton } from '@civilgrid/ui/components';
import type { ColumnDef } from '@tanstack/react-table';
import type { DateTime } from 'luxon';
import { useSnapshot } from 'valtio';
import { removeDigitizationAccess as apiRemoveAccess } from '~/api/civilsearch';
import { type AgencyDigitization, DigitizationType } from '~/common/types';
import { Notify } from '~/components2/Notify';
import DeleteDialog from '~/pages/admin/civilsearch/common/DeleteDialog';
import store from '../../store';

function getDate(d: DateTime) {
  return d.toFormat('MM/dd/yyyy');
}

export const columns: ColumnDef<AgencyDigitization>[] = [
  {
    id: 'type',
    header: 'Type',
    accessorKey: 'type',
    cell: ({ getValue }) => <span className="capitalize">{getValue<string>()}</span>,
  },
  {
    id: 'name',
    header: 'Project/Org Name',
    accessorFn: (d) => (d.type === DigitizationType.Organization ? d.organization?.name : d.project?.name) || '',
  },
  {
    id: 'id',
    header: 'Database ID',
    size: 250,
    accessorFn: (d) => (d.type === DigitizationType.Organization ? d.organization.id : d.project.id),
  },
  {
    id: 'projectId',
    header: 'Project ID (If Applicable)',
    accessorFn: (d) => (d.type === DigitizationType.Project ? d.project.projectId : ''),
  },
  {
    id: 'createdAt',
    header: 'Created At',
    size: 125,
    accessorFn: (d) => getDate(d.createdAt),
  },
  {
    id: 'updatedAt',
    header: 'Updated At',
    size: 125,
    accessorFn: (d) => getDate(d.updatedAt),
  },
  {
    id: 'actions',
    size: 25,
    cell: (cell) => {
      const { agency } = useSnapshot(store);
      const d = cell.row.original;

      const onDelete = async () => {
        try {
          const res = await apiRemoveAccess(agency!.id, d.id);
          if (!res.id) throw new Error('Bad response!');

          if (store.agency) {
            store.agency.digitization = res.digitization;
          }

          Notify.info({
            message: `Digitization access removed for ${
              d.type === DigitizationType.Project ? `project ${d.project.name}` : `organization ${d.organization.name}`
            }`,
          });
        } catch (e) {
          console.error('Error removing access: ', e);
          Notify.error({
            message: 'An error occurred while removing digitization access',
          });
        }
      };

      return (
        <div className="flex w-full items-center justify-center">
          <DeleteDialog
            title="Remove Digitization Access?"
            onConfirm={onDelete}
            data-testid={`agency-digitization-remove-dialog-${d.id}`}
            trigger={
              <IconButton
                variant="hollow"
                icon="trash"
                aria-label={`Remove digitization access for ${
                  d.type === DigitizationType.Organization ? d.organization.name : d.project.name
                }`}
                data-testid={`agency-digitization-remove-${d.id}`}
              />
            }
          >
            Confirm you want to remove <span className="font-bold">{agency?.agencyName}</span> digitization access for{' '}
            <span className="capitalize">{d.type}</span>{' '}
            <span className="font-bold">
              {d.type === DigitizationType.Organization ? d.organization.name : d.project.name}
            </span>
          </DeleteDialog>
        </div>
      );
    },
  },
];
